import { Injectable } from '@angular/core';
import { NetworkUtilsService, URL_DICT } from 'projects/app-core/src/service/network-utils.service';

@Injectable({ providedIn: 'root' })
export class PolicyService {
  constructor(private api: NetworkUtilsService) {}

  getContractSettings(companyId: string) {
    return this.api.get<ContractSettings>(URL_DICT.adminPolicyContractUrl + companyId);
  }

  updateContractSettings(companyId: string, data: ContractSettings) {
    return this.api.post(URL_DICT.adminPolicyContractUrl + companyId, data);
  }

  getPolicyRules(companyId: string) {
    return this.api.get<PolicyRule[]>(URL_DICT.adminPolicyRulesUrl + '/' + companyId);
  }

  createPolicyRule(payload: PolicyRulePayload) {
    return this.api.post(URL_DICT.adminPolicyRulesUrl, payload);
  }

  updatePolicyRule(ruleId: string, payload: PolicyRulePayload) {
    return this.api.post(URL_DICT.adminPolicyRulesUrl + '/' + ruleId, payload);
  }

  deletePolicyRule(ruleId: string) {
    return this.api.post(URL_DICT.adminPolicyRulesUrl + '/' + ruleId + '/delete', {});
  }

  getTherapistFilterRules(companyId: string) {
    return this.api.get<PolicyRule[]>(URL_DICT.adminPolicyTherapistFilterRulesUrl + '/' + companyId);
  }

  createTherapistFilterRule(payload: TherapistFilterRulePayload) {
    return this.api.post(URL_DICT.adminPolicyTherapistFilterRulesUrl, payload);
  }

  updateTherapistFilterRule(ruleId: string, payload: TherapistFilterRulePayload) {
    return this.api.post(URL_DICT.adminPolicyTherapistFilterRulesUrl + '/' + ruleId, payload);
  }

  deleteTherapistFilterRule(ruleId: string) {
    return this.api.post(URL_DICT.adminPolicyTherapistFilterRulesUrl + '/' + ruleId + '/delete', {});
  }

  getCompanyVisibility(companyId: string) {
    return this.api.get<CompanyVisibilityResponse>(URL_DICT.adminPolicyVisibilityUrl + companyId);
  }

  updateCompanyVisibility(companyId: string, payload: CompanyVisibilityUpdateRequest) {
    return this.api.post(URL_DICT.adminPolicyVisibilityUrl + companyId, payload);
  }

  getBenefitVisibility(companyId: string) {
    return this.api.get<BenefitVisibilityResponse>(URL_DICT.adminPolicyBenefitsUrl + companyId);
  }

  updateBenefitVisibility(companyId: string, payload: BenefitVisibilityUpdateRequest) {
    return this.api.post(URL_DICT.adminPolicyBenefitsUrl + companyId, payload);
  }

  getGlobalPolicyRules() {
    return this.api.get<PolicyRule[]>(URL_DICT.adminPolicyGlobalRulesUrl);
  }

  createGlobalPolicyRule(payload: GlobalPolicyRulePayload) {
    return this.api.post(URL_DICT.adminPolicyGlobalRulesUrl, payload);
  }

  updateGlobalPolicyRule(ruleId: string, payload: GlobalPolicyRulePayload) {
    return this.api.post(URL_DICT.adminPolicyGlobalRulesUrl + '/' + ruleId, payload);
  }

  deleteGlobalPolicyRule(ruleId: string) {
    return this.api.post(URL_DICT.adminPolicyGlobalRulesUrl + '/' + ruleId + '/delete', {});
}

  // Personal (per-user) policy rule overrides — APH-5224
  getUserPolicyRules(userId: string) {
    return this.api.get<PolicyRule[]>(URL_DICT.adminPolicyUserRulesUrl + '/' + userId);
  }

  createUserPolicyRule(payload: UserPolicyRulePayload) {
    return this.api.post(URL_DICT.adminPolicyUserRulesUrl, payload);
  }

  updateUserPolicyRule(ruleId: string, payload: UserPolicyRulePayload) {
    return this.api.post(URL_DICT.adminPolicyUserRulesUrl + '/' + ruleId, payload);
  }

  deleteUserPolicyRule(ruleId: string) {
    return this.api.post(URL_DICT.adminPolicyUserRulesUrl + '/' + ruleId + '/delete', {});
  }

  describeRule(ruleId: string) {
    return this.api.post<{ description: string }>(
      URL_DICT.adminPolicyRuleDescribeUrl + ruleId + '/describe',
      {},
    );
  }
}

export interface ContractSettings {
  companyId?: string;
  companyName?: string;
  policyPoolMode?: 'individual' | 'company';
  policyPurchasedSessionPool?: number;
  /** Vertical IDs whose sessions draw from the shared company pool. Empty = all verticals. */
  policyPoolVerticalIds?: string[];
  policyAllowSelfPayOnPoolExhaust?: boolean;
  policyControlsEnabled?: boolean;
  anyoGoalsEnabled?: boolean;
  /** GST percent on the self-pay markup (markup itself is set per-rule — see selfPayMarkupType/Value on a Session Booking rule's outputs). Mandatory whenever a rule's markup is set, no on/off toggle. */
  policySelfPayMarkupGstPercent?: number;
  /** GST on the base self-pay amount (slab rate or flat amount) — has an on/off toggle, unlike markup GST. */
  policySelfPayBaseGstEnabled?: boolean;
  policySelfPayBaseGstPercent?: number;
  reengageEnabled?: boolean;
}

export interface PolicyRule {
  id: string;
  name: string;
  description?: string;
  priority: number;
  active: boolean;
  action: string;
  isGlobal: boolean;
  companyId: string;
  /** Set only on a personal (per-user) rule override — read-only display field, never set from this repo's company/global forms. */
  userId?: string;
  scope: { verticalId?: string[]; concernId?: string[]; expertLevelId?: string; therapistIds?: string[] };
  conditions: unknown;
  outputs: unknown;
}

export interface PolicyRulePayload {
  name: string;
  priority: number;
  active: boolean;
  action: string;
  companyId: string;
  scope: { verticalId?: string[]; concernId?: string[]; expertLevelId?: string; therapistIds?: string[] };
  conditions: unknown;
  outputs: unknown;
}

// Global rule payload — no companyId or owner (backend hardcodes Global)
export interface GlobalPolicyRulePayload {
  name: string;
  priority: number;
  active: boolean;
  action: string;
  scope: { verticalId?: string[]; concernId?: string[]; expertLevelId?: string; therapistIds?: string[] };
  conditions: unknown;
  outputs: unknown;
}

// Personal (per-user) rule payload — mirrors PolicyRulePayload plus the required userId.
// companyId is the user's CURRENT company, resolved by the caller (search-user.component).
export interface UserPolicyRulePayload extends PolicyRulePayload {
  userId: string;
}

export interface TherapistFilterRulePayload {
  name: string;
  priority: number;
  active: boolean;
  companyId: string;
  conditions: unknown;
  outputs: Record<string, unknown>;
}

export interface CompanyVisibilityConcern {
  concernId: string;
  concernName: string;
  hidden: boolean;
}

export interface CompanyVisibilityVerticalRow {
  verticalId: string;
  verticalName: string;
  visible: boolean;
  concerns: CompanyVisibilityConcern[];
}

export interface CompanyVisibilityResponse {
  companyId: string;
  verticals: CompanyVisibilityVerticalRow[];
  hiddenVerticalIds: string[];
  hiddenConcernIds: string[];
}

export interface CompanyVisibilityUpdateRequest {
  hiddenVerticalIds: string[];
  hiddenConcernIds: string[];
}

export type ContractBenefit =
  | 'THERAPY_SESSIONS'
  | 'EVENTS_AND_CLASSES'
  | 'DIAGNOSTICS'
  | 'LYFIS'
  | 'RHYTHM'
  | 'FITNESS'
  | 'HEALTH_PROGRAMS'
  | 'KINESTEX'
  | 'CIRCLES'
  | 'CHALLENGES'
  | 'RESOURCE_LIBRARY';

export const ALL_CONTRACT_BENEFITS: ContractBenefit[] = [
  'THERAPY_SESSIONS',
  'EVENTS_AND_CLASSES',
  'DIAGNOSTICS',
  'LYFIS',
  'RHYTHM',
  'FITNESS',
  'HEALTH_PROGRAMS',
  'KINESTEX',
  'CIRCLES',
  'CHALLENGES',
  'RESOURCE_LIBRARY',
];

export interface BenefitVisibilityResponse {
  companyId: string;
  benefits: Partial<Record<ContractBenefit, boolean>>;
}

export interface BenefitVisibilityUpdateRequest {
  benefit: ContractBenefit;
  visible: boolean;
}
